import { FunctionComponent, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import {
  desktopCenteredContentCSS,
  mobileCSS,
  tabletCSS,
} from '@measures/responsive'
import usePageMetadata from '@hooks/usePageMetadata'
import LayoutComponents from './Layouts'
import Empty from '@components/Empty'
import { extractPageType } from '@utils/cook'
import useIsContentForceCentered from '@hooks/useIsContentForceCentered'
import useIsErrorPage from '@hooks/useIsErrorPage'
import { printCSS } from '@utils/style'

const COLLAPSE_THRESHOLD = 20

const { HomeSection, FirstLevelSection, OtherSection, Article } =
  LayoutComponents

const StyledSecondRow = styled.div<{
  isContentForceCentered: boolean
  isCollapsed: boolean | null
}>`
  ${({
    theme: {
      colors: {
        grey: { grey_10, grey_30 },
      },
    },
    isCollapsed,
    isContentForceCentered,
  }) =>
    css`
      position: relative;
      height: 48px;
      background-color: ${grey_10};
      box-shadow: 0px 1px 2px ${grey_30};
      top: 0px;
      display: grid;
      grid-template-columns: 994px;
      ${printCSS(css`
        display: none;
      `)}

      ${isCollapsed !== null &&
      css`
        transition: top 0.3s;
      `}

      ${isCollapsed &&
      css`
        top: -48px;
        height: 0px;
        transition: top 0.3s, height 0.3s;
      `}

      ${desktopCenteredContentCSS(css`
        justify-content: center;
      `)}

      ${isContentForceCentered &&
      css`
        justify-content: center;
      `}

      ${tabletCSS(css`
        grid-template-columns: 1fr;
        padding-right: 16px;
      `)}

      ${mobileCSS(css`
        grid-template-columns: 1fr;
        box-shadow: 0px 2px 4px ${grey_30};
        padding-right: 16px;
      `)}
    `}
`

const SecondRow: FunctionComponent = () => {
  const [isCollapsed, setIsCollapsed] = useState<boolean | null>(null)
  const lastScrollPosition = useRef<number>(0)

  const isContentForceCentered = useIsContentForceCentered()
  const isErrorPage = useIsErrorPage()
  const pageMetadata = usePageMetadata()

  let Content: FunctionComponent<any> = Empty

  const isArticle = extractPageType(pageMetadata) === 'article'

  const handleScroll = () => {
    // Because of elastic scrolling on certain smartphones, we have to also check
    // negative values of scrollY
    if (
      window.scrollY <= 0 ||
      window.scrollY + COLLAPSE_THRESHOLD < lastScrollPosition.current
    ) {
      setIsCollapsed(false)
    } else if (
      window.scrollY - COLLAPSE_THRESHOLD >
      lastScrollPosition.current
    ) {
      setIsCollapsed(true)
    }

    // Set the last scroll position to the current scrollY, so that we can check
    // if the user scrolled up or down
    lastScrollPosition.current = window.scrollY
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    // On page change set the collapsed state to null, so that the slide down transition
    // is not triggered when the user changes to a different page
    setIsCollapsed(null)
  }, [pageMetadata])

  if (isArticle) {
    Content = Article
  } else if (['home'].includes(pageMetadata.context) || isErrorPage) {
    Content = HomeSection
  } else if (pageMetadata.breadcrumbs.length === 2) {
    Content = FirstLevelSection
  } else if (pageMetadata.breadcrumbs.length > 2) {
    Content = OtherSection
  }

  return (
    <StyledSecondRow
      isCollapsed={isCollapsed}
      isContentForceCentered={isContentForceCentered}>
      <Content />
    </StyledSecondRow>
  )
}

export default SecondRow
